import {
  all,
  call,
  put,
  select,
  take,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import {
  create_formula_feedback,
  get_formula_feedback_for_order,
  create_formula_request,
  list_formula_support_cases,
  list_formula_support_notifications,
  mark_formula_support_case_seen,
  archive_formula_support_cases,
} from 'js/api/FormulaSupport';
import webRequest from './WebRequest';
import actions, {
  actionTypes,
  selectors as supportSelectors,
} from '../reducers/FormulaSupport';
import configActions, {
  actionTypes as configActionTypes,
  selectors as configSelectors,
} from '../reducers/Configuration';
import { selectors as cacheSelectors } from '../reducers/Cache';
import orderActions, { RETINT_TYPE_FORMULA } from '../reducers/Order';
import { setOrderitem } from './OrderFetching';
import {
  isStandalone,
  resolveFormula,
  supportContext,
} from '../../mylib/FormulaSupport';
import { hasPrivilege } from '../../mylib/Privileges';
import { INFO_CARD, SOURCE_USER, offline_mode } from '../../Constants';

function* siteId() {
  const site = yield select(configSelectors.site);
  return site?.siteid;
}

function* canReadSupportQueue() {
  const config = yield select(configSelectors.config_values);
  return (
    !offline_mode &&
    !(yield select(isStandalone)) &&
    !!config?.enable_formula_support_queue &&
    hasPrivilege('order_formula_support_queue')
  );
}

function* fetchCases(action) {
  if (!(yield call(canReadSupportQueue))) {
    yield put({ type: actionTypes.FETCH_SKIPPED });
    return;
  }
  try {
    const siteid = yield call(siteId);
    if (siteid == null) throw new Error('Site unavailable');
    const response = yield call(
      webRequest,
      list_formula_support_cases(siteid, action.payload)
    );
    yield put(actions.set(response.data));
  } catch (error) {
    yield put({ type: 'FORMULA_SUPPORT/ERROR', payload: error });
  }
}

function* refreshCases() {
  const state = yield select(supportSelectors.state);
  if (state.loaded) {
    yield put(actions.fetch({ ...state.query, page: 1 }));
  }
}

function* fetchNotifications() {
  if (!(yield call(canReadSupportQueue))) {
    yield put({ type: actionTypes.NOTIFICATIONS_SKIPPED });
    return;
  }
  try {
    const siteid = yield call(siteId);
    if (siteid == null) throw new Error('Site unavailable');
    const response = yield call(
      webRequest,
      list_formula_support_notifications(siteid)
    );
    yield put(actions.setNotifications(response.data));
  } catch (error) {
    yield put({ type: actionTypes.NOTIFICATIONS_ERROR, payload: error });
  }
}

function* refreshChanged() {
  yield all([call(refreshCases), call(fetchNotifications)]);
}

function* createCase(action, creator) {
  const config = yield select(configSelectors.config_values);
  const privilege =
    creator === create_formula_feedback
      ? 'order_formula_feedback'
      : 'order_formula_request';
  const enabled =
    creator === create_formula_feedback
      ? config?.enable_formula_feedback
      : config?.enable_formula_request;
  if (
    offline_mode ||
    (yield select(isStandalone)) ||
    !enabled ||
    !hasPrivilege(privilege)
  ) {
    yield put({ type: actionTypes.UNAVAILABLE });
    return;
  }
  const siteid = yield call(siteId);
  if (siteid == null) return;
  try {
    const zone = yield select(configSelectors.zone);
    if (!Number.isInteger(zone?.zoneid) || zone.zoneid <= 0)
      throw new Error('Zone unavailable');
    const response = yield call(
      webRequest,
      creator(siteid, { ...action.payload, zoneid: zone.zoneid })
    );
    yield put({ type: actionTypes.CREATE_SUCCEEDED });
    yield put(actions.add(response.data));
    yield call(fetchNotifications);
  } catch (error) {
    if (
      creator === create_formula_feedback &&
      error?.response?.status === 409 &&
      error.response.data?.case
    ) {
      yield put({
        type: actionTypes.FEEDBACK_EXISTS,
        payload: {
          orderid: action.payload.orderid,
          case: error.response.data.case,
        },
      });
      return;
    }
    yield put({ type: 'FORMULA_SUPPORT/ERROR', payload: error });
  }
}

function* lookupOrder(action) {
  if (offline_mode || (yield select(isStandalone))) {
    yield put({ type: actionTypes.UNAVAILABLE });
    return;
  }
  try {
    const siteid = yield call(siteId);
    if (siteid == null) throw new Error('Site unavailable');
    const response = yield call(
      webRequest,
      get_formula_feedback_for_order(siteid, action.payload)
    );
    yield put({
      type: actionTypes.LOOKUP_SUCCEEDED,
      payload: response.data.case,
    });
  } catch (error) {
    yield put({ type: actionTypes.LOOKUP_FAILED, payload: error });
  }
}

function* markSeen(action) {
  if (!(yield call(canReadSupportQueue))) {
    yield put({ type: actionTypes.UNAVAILABLE });
    return;
  }
  try {
    const siteid = yield call(siteId);
    if (siteid == null) throw new Error('Site unavailable');
    const response = yield call(
      webRequest,
      mark_formula_support_case_seen(siteid, action.payload)
    );
    yield put(actions.updated([response.data]));
    yield call(fetchNotifications);
  } catch (error) {
    yield put({ type: 'FORMULA_SUPPORT/ERROR', payload: error });
  }
}

function* archiveCases(action) {
  if (!(yield call(canReadSupportQueue))) {
    yield put({ type: actionTypes.UNAVAILABLE });
    return;
  }
  try {
    const siteid = yield call(siteId);
    if (siteid == null) throw new Error('Site unavailable');
    const response = yield call(
      webRequest,
      archive_formula_support_cases(siteid, action.payload)
    );
    yield put({ type: actionTypes.ARCHIVED, payload: response.data });
    yield call(refreshCases);
    yield call(fetchNotifications);
  } catch (error) {
    yield put({ type: actionTypes.ERROR, payload: error });
  }
}

function* useFormula(action) {
  try {
    if (!(yield call(canReadSupportQueue))) {
      yield put({ type: actionTypes.UNAVAILABLE });
      return;
    }
    const cases = yield select(supportSelectors.cases);
    const item = cases.find((entry) => entry.supportcaseid === action.payload);
    const { response } = supportContext(item);
    const snapshot = response?.formula;
    if (
      offline_mode ||
      !hasPrivilege('order_formula_support_queue') ||
      !hasPrivilege('order_local_formula') ||
      !item ||
      item.status === 'ARCHIVED' ||
      !response ||
      !Number.isInteger(response.zoneid) ||
      response.zoneid <= 0
    )
      throw new Error('Formula cannot be used');

    let zone = yield select(configSelectors.zone);
    if (zone?.zoneid !== response.zoneid) {
      const zones = yield select(cacheSelectors.zones);
      const targetZone = zones.find(
        (availableZone) => availableZone.zoneid === response.zoneid
      );
      if (!targetZone) throw new Error('Formula zone unavailable');
      yield put(configActions.setZone(targetZone));
      yield take(configActionTypes.SET_ZONE_FULLFILLED);
      zone = yield select(configSelectors.zone);
    }

    const products = yield select(cacheSelectors.products);
    const cntcodemap = yield select(cacheSelectors.cntcodemap);
    const config = yield select(configSelectors.config_values);
    const resolved = resolveFormula(response, products, cntcodemap);
    if (
      zone?.zoneid !== response.zoneid ||
      !config.enable_formula_support_queue ||
      resolved.problem
    )
      throw new Error('Formula cannot be used');
    const { product, base, can, components: resolvedComponents } = resolved;
    // Whitelist the snapshot fields: never carry order identity, discounts,
    // addition-only quantities or historical dispense state into a new order.
    const data = {
      zoneid: response.zoneid,
      productid: product.productid,
      productname: product.productzname,
      baseid: base.baseid,
      basecode: base.basecode,
      canid: can.canid,
      cansizeid: can.cansizeid,
      cansizecode: can.cansizecode,
      basevolume: snapshot.basevolume,
      nominalamount: can.nominalamount,
      gravimetricnominal: Boolean(can.gravimetric),
      colourcode: response.colourcode,
      colourname: response.colourname,
      rgb: response.rgb,
      cardname: response.colourcard,
      source: SOURCE_USER,
      cntsinitem: resolvedComponents.map(({ cntid, cntcode, volume }) => ({
        cntid,
        cntcode,
        volume,
      })),
      ncans: 1,
      dispenses: [],
      localorder: { customerid: null },
    };
    yield put(orderActions.clearOrder());
    yield call(setOrderitem, data, RETINT_TYPE_FORMULA, false);
    yield put(orderActions.setOpenSection(INFO_CARD));
    yield put({ type: actionTypes.USED });
  } catch (error) {
    yield put({ type: actionTypes.ERROR, payload: error });
  }
}

export default function* formulaSupportSaga() {
  yield all([
    takeLatest(actionTypes.FETCH, fetchCases),
    takeLatest(actionTypes.LOOKUP_ORDER, lookupOrder),
    takeLatest(actionTypes.CHANGED, refreshChanged),
    takeLatest(actionTypes.SOCKET_CONNECTED, refreshChanged),
    takeLatest(actionTypes.FETCH_NOTIFICATIONS, fetchNotifications),
    takeLatest(actionTypes.CREATE_FEEDBACK, function* (action) {
      yield call(createCase, action, create_formula_feedback);
    }),
    takeLatest(actionTypes.CREATE_REQUEST, function* (action) {
      yield call(createCase, action, create_formula_request);
    }),
    takeEvery(actionTypes.MARK_SEEN, markSeen),
    takeLatest(actionTypes.ARCHIVE, archiveCases),
    takeLatest(actionTypes.USE, useFormula),
  ]);
}
